import type { WhatsAppInboxChat } from '@zrsystem/contracts';
import {
  Avatar,
  Badge,
  Empty,
  EmptyIcon,
  EmptyText,
  EmptyTitle,
  ListRow,
  ListRowAside,
  ListRowAsideValue,
  ListRowMain,
  ListRowSubtitle,
  ListRowTitle,
  ListRows,
  ShellIconWhatsApp,
  Spinner,
  listTablePrimaryInitials,
} from '@zrsystem/ui';
import { useMemo, type ReactElement, type ReactNode } from 'react';

import {
  whatsAppInboxChatDisplayName,
  whatsAppInboxMessagePreviewLabel,
} from '@/features/notification/lib/whatsapp-inbox-labels.js';
import { formatWhatsAppInboxTimestamp } from '@/features/notification/lib/whatsapp-inbox-timestamp.js';
import { ChatListSearch } from '@/shared/chat/chat-list-search.js';
import { chatMatchesSearch } from '@/shared/chat/match-chat-query.js';

export interface WhatsAppInboxChatRailProps {
  readonly chats: readonly WhatsAppInboxChat[];
  readonly selectedChatId: string | null;
  readonly isLoading: boolean;
  readonly errorMessage: string | null;
  readonly searchQuery: string;
  readonly onSearchQueryChange: (value: string) => void;
  readonly emptyState?: ReactNode;
  readonly onSelectChat: (chatId: string) => void;
}

function whatsAppChatSearchFields(chat: WhatsAppInboxChat): readonly (string | null)[] {
  const at = chat.id.indexOf('@');
  const localId = at === -1 ? chat.id : chat.id.slice(0, at);
  return [
    whatsAppInboxChatDisplayName(chat),
    chat.name,
    chat.lastMessage === null ? null : chat.lastMessage.body,
    whatsAppInboxMessagePreviewLabel(chat.lastMessage),
    localId,
  ];
}

export function WhatsAppInboxChatRail({
  chats,
  selectedChatId,
  isLoading,
  errorMessage,
  searchQuery,
  onSearchQueryChange,
  emptyState,
  onSelectChat,
}: WhatsAppInboxChatRailProps): ReactElement {
  const searching = searchQuery.trim().length > 0;
  const visibleChats = useMemo(
    () => chats.filter((chat) => chatMatchesSearch(searchQuery, whatsAppChatSearchFields(chat))),
    [chats, searchQuery],
  );

  return (
    <div className="whatsapp-inbox__rail">
      <div className="whatsapp-inbox__rail-header">
        <ShellIconWhatsApp size={16} />
        <strong>WhatsApp</strong>
      </div>
      <ChatListSearch value={searchQuery} onChange={onSearchQueryChange} />
      <div className="whatsapp-inbox__rail-scroll">
        {isLoading ? <Spinner aria-label="Carregando chats" /> : null}
        {errorMessage !== null ? <p className="whatsapp-inbox__feedback">{errorMessage}</p> : null}
        {!isLoading && errorMessage === null && chats.length === 0
          ? (emptyState ?? (
              <Empty>
                <EmptyIcon>
                  <ShellIconWhatsApp size={22} />
                </EmptyIcon>
                <EmptyTitle>Nenhum chat</EmptyTitle>
                <EmptyText>As conversas do WhatsApp da unidade aparecem aqui.</EmptyText>
              </Empty>
            ))
          : null}
        {!isLoading &&
        errorMessage === null &&
        searching &&
        chats.length > 0 &&
        visibleChats.length === 0 ? (
          <Empty>
            <EmptyTitle>Nenhuma conversa encontrada</EmptyTitle>
            <EmptyText>Tente outro nome ou trecho da conversa.</EmptyText>
          </Empty>
        ) : null}
        {visibleChats.length > 0 ? (
          <ListRows>
            {visibleChats.map((chat) => {
              const title = whatsAppInboxChatDisplayName(chat);
              const preview = whatsAppInboxMessagePreviewLabel(chat.lastMessage);
              const timeLabel =
                chat.lastMessage !== null
                  ? formatWhatsAppInboxTimestamp(chat.lastMessage.timestamp)
                  : '';

              return (
                <ListRow
                  key={chat.id}
                  selected={chat.id === selectedChatId}
                  onClick={() => {
                    onSelectChat(chat.id);
                  }}
                  aria-label={
                    chat.unreadCount > 0
                      ? `${title}, ${chat.unreadCount} mensagens não lidas`
                      : title
                  }
                >
                  {chat.picture !== null ? (
                    <Avatar size="lg" src={chat.picture} alt="" aria-hidden />
                  ) : (
                    <Avatar size="lg" aria-hidden>
                      {listTablePrimaryInitials(title)}
                    </Avatar>
                  )}
                  <ListRowMain>
                    <ListRowTitle>{title}</ListRowTitle>
                    <ListRowSubtitle className="whatsapp-inbox__preview">{preview}</ListRowSubtitle>
                  </ListRowMain>
                  <ListRowAside>
                    <ListRowAsideValue>{timeLabel}</ListRowAsideValue>
                    {chat.unreadCount > 0 ? (
                      <Badge variant="solid-primary">
                        {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
                      </Badge>
                    ) : null}
                  </ListRowAside>
                </ListRow>
              );
            })}
          </ListRows>
        ) : null}
      </div>
    </div>
  );
}
